import { apiClient } from "@/lib/api/client";

export interface MSNUserSnapshot {
  id: string;
  full_name: string;
  avatar_url: string | null;
  role: "patient" | "professional";
  verified: boolean;
  specialty: string | null;
}

export interface MSNAttachment {
  id: string;
  file_name: string | null;
  file_type: string;
  mime: string;
  size: number;
  scan_status: "pending" | "clean" | "blocked";
  created_at: string;
  download_allowed: boolean;
}

export interface MSNMessage {
  id: string;
  conversation_id: string;
  type: "text" | "attachment" | "system" | "disclaimer";
  sender: MSNUserSnapshot;
  body: string | null;
  attachment: MSNAttachment | null;
  metadata: Record<string, unknown>;
  created_at: string;
  edited_at: string | null;
  delivered_at: string | null;
  read_at: string | null;
  is_delivered: boolean;
  is_read: boolean;
}

export interface MSNConversation {
  id: string;
  type: string;
  participants: MSNUserSnapshot[];
  last_message_preview: string | null;
  last_message_at: string | null;
  unread_count: number;
  created_at: string;
  updated_at: string;
}

export interface MSNDisclaimer {
  key: string;
  text: string;
  severity: "info" | "warning";
  version: number;
  acknowledged: boolean;
}

interface ListResponse<T> {
  items: T[];
}

export const getMsnConversations = async (): Promise<MSNConversation[]> => {
  const response = await apiClient.get<ListResponse<MSNConversation>>("/msn/conversations");
  return response.data.items;
};

export const getMsnMessages = async (conversationId: string): Promise<MSNMessage[]> => {
  const response = await apiClient.get<ListResponse<MSNMessage>>(`/msn/conversations/${conversationId}/messages`);
  return response.data.items;
};

export const sendMsnMessage = async (conversationId: string, text: string): Promise<MSNMessage> => {
  const response = await apiClient.post<MSNMessage>(`/msn/conversations/${conversationId}/messages`, { text });
  return response.data;
};

export const markMsnRead = async (conversationId: string, messageId: string): Promise<void> => {
  await apiClient.post(`/msn/conversations/${conversationId}/read`, { message_id: messageId });
};

export const getMsnDisclaimers = async (conversationId: string): Promise<MSNDisclaimer[]> => {
  const response = await apiClient.get<ListResponse<MSNDisclaimer>>(`/msn/conversations/${conversationId}/disclaimers`);
  return response.data.items;
};

export const getMsnUnreadCount = async (): Promise<number> => {
  const response = await apiClient.get<{ unread_count: number }>("/msn/unread-count");
  return response.data.unread_count;
};

export const createMsnConversation = async (
  patientId: string,
  professionalId: string,
): Promise<MSNConversation> => {
  const response = await apiClient.post<MSNConversation>("/msn/conversations", {
    patient_id: patientId,
    professional_id: professionalId,
  });
  return response.data;
};
